'use client';

import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import * as React from 'react';
import { createContext } from '~logic/lib/react';
import { useMediaQuery, useShorterThanPx } from '~ui/mediaQueries/useMediaQuery'; // base layout breakpoint values: (https://www.vzhurudolu.cz/prirucka/breakpointy#univerzalni)
//  xs:    0 -  599 (mobile)
//  sm:  600 -  899 (tablet)
//  md:  900 - 1199 (desktop – small/narrow desktop)
//  lg: 1200 - 1799 (desktop – medium desktop)
//  xl: 1800 - ++++ (desktop – large/wide desktop)

import { jsx as _jsx } from "react/jsx-runtime";
var shortScreenMaxHeight = 900;
var smMin = 600;
var mdMin = 900;
var lgMin = 1200;
var xlMin = 1800;
var xsMax = smMin - 1;
var smMax = mdMin - 1;
var mdMax = lgMin - 1;
var lgMax = xlMin - 1;

var _createContext = createContext("ResponsiveLayoutXS"),
    _createContext2 = _slicedToArray(_createContext, 2),
    ProviderXS = _createContext2[0],
    useLayoutIsXS = _createContext2[1];

var _createContext3 = createContext("ResponsiveLayoutSM"),
    _createContext4 = _slicedToArray(_createContext3, 2),
    ProviderSM = _createContext4[0],
    useLayoutIsSM = _createContext4[1];

var _createContext5 = createContext("ResponsiveLayoutMD"),
    _createContext6 = _slicedToArray(_createContext5, 2),
    ProviderMD = _createContext6[0],
    useLayoutIsMD = _createContext6[1];

var _createContext7 = createContext("ResponsiveLayoutLG"),
    _createContext8 = _slicedToArray(_createContext7, 2),
    ProviderLG = _createContext8[0],
    useLayoutIsLG = _createContext8[1];

var _createContext9 = createContext("ResponsiveLayoutXL"),
    _createContext10 = _slicedToArray(_createContext9, 2),
    ProviderXL = _createContext10[0],
    useLayoutIsXL = _createContext10[1];

var _createContext11 = createContext("ResponsiveLayoutShortScreen"),
    _createContext12 = _slicedToArray(_createContext11, 2),
    ProviderShortScreen = _createContext12[0],
    useLayoutIsShort = _createContext12[1]; // if you need to combine some values (eg. isMDPlus) use combined values (actually the opposite values as commented bellow)
// and memoize the result
// also consider adding more providers for common cases


var _createContext13 = createContext("ResponsiveLayoutLGPlus"),
    _createContext14 = _slicedToArray(_createContext13, 2),
    ProviderLGPlus = _createContext14[0],
    useLayoutIsLGPlus = _createContext14[1];

var _createContext15 = createContext("ResponsiveLayoutSMMinus"),
    _createContext16 = _slicedToArray(_createContext15, 2),
    ProviderSMMinus = _createContext16[0],
    useLayoutIsSMMinus = _createContext16[1];

var _createContext17 = createContext("ResponsiveLayoutMDMinus"),
    _createContext18 = _slicedToArray(_createContext17, 2),
    ProviderMDMinus = _createContext18[0],
    useLayoutIsMDMinus = _createContext18[1];

// TODO SSR ???
// - everything is false when window.matchMedia is not defined
// - flip it to true (if on server), render everything and then hide on the client?
// - more CSS MQs?

/**
 * context provider grouping multiple app-wide responsive layout flags
 * use just what you need (useLayoutIsXS, useLayoutIsSM, useLayoutIsMD, useLayoutIsLG, useLayoutIsXL)
 */
export var ResponsiveLayoutProvider = function ResponsiveLayoutProvider(_ref) {
  var children = _ref.children;
  // useMediaQuery wraps its content in `()`
  // that’s why there might be some strange `)(` sequences in those queries bellow
  // when combining multiple conditions
  var isXS = useMediaQuery("max-width: ".concat(xsMax, "px"));
  var isSM = useMediaQuery("min-width: ".concat(smMin, "px) and (max-width: ").concat(smMax, "px"));
  var isMD = useMediaQuery("min-width: ".concat(mdMin, "px) and (max-width: ").concat(mdMax, "px"));
  var isLG = useMediaQuery("min-width: ".concat(lgMin, "px) and (max-width: ").concat(lgMax, "px"));
  var isXL = useMediaQuery("min-width: ".concat(xlMin, "px"));
  var isShortScreen = useShorterThanPx({
    breakpointPx: shortScreenMaxHeight
  });
  var isSMMinus = !(isMD || isLG || isXL);
  return /*#__PURE__*/_jsx(ProviderXS, {
    value: isXS,
    children: /*#__PURE__*/_jsx(ProviderSM, {
      value: isSM,
      children: /*#__PURE__*/_jsx(ProviderMD, {
        value: isMD,
        children: /*#__PURE__*/_jsx(ProviderLG, {
          value: isLG,
          children: /*#__PURE__*/_jsx(ProviderXL, {
            value: isXL,
            children: /*#__PURE__*/_jsx(ProviderShortScreen, {
              value: isShortScreen,
              children: /*#__PURE__*/_jsx(ProviderLGPlus // using isLG || isXL would cause one tick of "false" exactly on the breakpoint value (1800)
              // because fist the isLG is updated from true to false while isXL stays false
              // only then in the next tick the isXL is updated to true & the result is corrected
              // extending the isLG range to max = xlMin would cause other problems (rendering both lg & xl stuff) that that’s not a valid option as well
              , {
                value: !(isXS || isSM || isMD),
                children: /*#__PURE__*/_jsx(ProviderSMMinus, {
                  value: isSMMinus,
                  children: /*#__PURE__*/_jsx(ProviderMDMinus, {
                    value: isMD || isSMMinus,
                    children: children
                  })
                })
              })
            })
          })
        })
      })
    })
  });
};
export { useLayoutIsXS, useLayoutIsSM, useLayoutIsMD, useLayoutIsLG, useLayoutIsXL };
export { useLayoutIsLGPlus, useLayoutIsSMMinus, useLayoutIsMDMinus };
export { useLayoutIsShort };
export var LAYOUT_BREAKPOINTS = {
  smMin: smMin,
  mdMin: mdMin,
  lgMin: lgMin,
  xlMin: xlMin,
  xsMax: xsMax,
  smMax: smMax,
  mdMax: mdMax,
  lgMax: lgMax,
  shortScreenMaxHeight: shortScreenMaxHeight
};